import {
    combinedStatuses,
    CompleteSortieCommand,
    ConcreteMappedEntityConcreteFormDto,
    ContactWithLocationDto,
    DateFormatService,
    defaultBuildFormAttachmentUrl,
    DisplayableMissionDto,
    getStatusCategory,
    httpParamSerializer,
    MissionLocation,
    MissionLocationPoint,
    MissionNoticeDto,
    MissionRoleDto,
    NotamDto,
    RequestersMissionApprovalDto,
    RequiringEntity,
    SimpleOrganisationDto,
    SortieDto
} from '@flyfreely-portal-ui/flyfreely';
import { FeatureGroup } from '@flyfreely-portal-ui/map';
import {
    FormatDurationPipe,
    NOTAM_ENTITIES,
    setupStatusFormatter
} from '@flyfreely-portal-ui/ui';
import { circle } from '@turf/circle';
import { Feature, FeatureCollection } from 'geojson';
import { toSimpleAuthority } from 'libs/authorities/src/lib/helpers';
import moment from 'moment-timezone';
import { MissionApprovalWithAuthority } from './interfaces';
import { PersonWithRegisterStatus } from './mission-edit-v2/mission-edit.service';

export function approvalRequiringEntities(
    approval: RequestersMissionApprovalDto
): RequiringEntity[] {
    if (
        approval == null ||
        approval.status === RequestersMissionApprovalDto.Status.CANCELLED ||
        approval.status === RequestersMissionApprovalDto.Status.REJECTED
    ) {
        return [];
    }
    return [
        {
            requiringEntityType: 'MissionApproval',
            requiringEntityId: approval.id,
            name: 'Mission Approval 1'
        }
    ];
}

export interface ApprovalScreenStatus {
    readOnly: boolean;
    isPending: boolean;
    isBeingReviewed: boolean;
    canAttachFiles: boolean;
}

/**
 * Find the RPIC in the crew list
 * @param missionCrew the crew list
 */
export function findRpic<T>(
    missionCrew: {
        person?: T;
        role?: { coreRole?: MissionRoleDto.CoreRole };
    }[]
): T | undefined {
    return missionCrew
        .filter(c => c.role.coreRole === 'PILOT_IN_COMMAND')
        .map(c => c.person)[0];
}

/**
 * Derive the mission jurisdiction.
 * @param mission the full mission object
 */
export function findMissionAirspaceJurisdictionId(
    mission: DisplayableMissionDto
) {
    return mission.missionWorkflowVersion?.delegatedAuthority?.authorityType
        ?.jurisdiction != null
        ? mission.missionWorkflowVersion.delegatedAuthority.authorityType
              .jurisdiction.id
        : mission.location?.airspaceJurisdiction?.id;
}

export const missionRegisterStatusOrder = [
    'ACTIVE',
    'PENDING',
    'EXPIRED',
    'SUSPENDED',
    'NOT_ON_REGISTER',
    'NOT_AVAILABLE'
];

export function sortMissionRegisterStatus(
    a: PersonWithRegisterStatus,
    b: PersonWithRegisterStatus
) {
    /* if (
        !(
            missionRegisterStatusOrder.includes(a.registerStatus) &&
            missionRegisterStatusOrder.includes(b.registerStatus)
        )
    ) {
        return +1;
    } else if (
        missionRegisterStatusOrder.findIndex(s => s === a.registerStatus) <
        missionRegisterStatusOrder.findIndex(s => s === b.registerStatus)
    ) {
        return -1;
    } else if (
        missionRegisterStatusOrder.findIndex(s => s === a.registerStatus) >
        missionRegisterStatusOrder.findIndex(s => s === b.registerStatus)
    ) {
        return +1;
    } else {
        return 1;
    } */
    // FIXME: this sorting won't work with multiple registers or GraphQL and needs revision or removal.
    return 1;
}

export function aerodromesToFeatureGroups(
    aerodromes: ContactWithLocationDto[],
    featureGroupId = 0
): FeatureGroup[] {
    return aerodromes != null
        ? [
              {
                  canAdd: false,
                  id: featureGroupId,
                  name: 'Aerodromes',
                  type: 'Point',
                  existingFeatures: aerodromes.map(a => ({
                      name: a.name,
                      id: `aerodrome-${a.identifier}`,
                      geom: a.location
                  }))
              }
          ]
        : [];
}

export function notamToFeatureCollection(
    notamList: NotamDto[]
): FeatureCollection | null {
    return notamList != null && notamList.length > 0
        ? {
              type: 'FeatureCollection',
              features: notamList.map(a => ({
                  type: 'Feature',
                  geometry:
                      a.geometry != null && a.geometry.type === 'Polygon'
                          ? a.geometry
                          : circle(a.position, a.radius * 1.852).geometry,
                  properties: {
                      id: `notam-${a.id}`,
                      notam_id: a.id,
                      notam_text: a.message,
                      notam_entity: NOTAM_ENTITIES[a.entity] ?? ''
                  }
              }))
          }
        : null;
}

/**
 * Returns true when the location list contains only point data (no mission detail).
 */
function isPointOnlyList(
    locationList: MissionLocation[] | MissionLocationPoint[]
): locationList is MissionLocationPoint[] {
    return (
        locationList.length === 0 ||
        (locationList[0] as MissionLocation).missionList == null
    );
}

/**
 * Compute the mission feature collections.
 *
 * Accepts either a full `MissionLocation[]` (zoom >= 12) or a lightweight
 * `MissionLocationPoint[]` (zoom < 12). When only point data is available the
 * returned polygon collection is empty.
 *
 * @param locationList full or points-only location list
 * @param dateFormatService
 * @returns [pointFeatureCollection, polygonFeatureCollection]
 */
export function missionLocationsToFeatureCollection(
    locationList: MissionLocation[] | MissionLocationPoint[],
    dateFormatService: DateFormatService
): [FeatureCollection, FeatureCollection] | [null, null] {
    if (locationList == null) {
        return [null, null];
    }

    // Points-only path (low zoom)
    if (isPointOnlyList(locationList)) {
        const pointFeatures: Feature[] = (
            locationList as MissionLocationPoint[]
        ).map(location => ({
            type: 'Feature' as const,
            geometry: location.representativePoint,
            id: `mission-point-location-${location.id}`,
            properties: {
                id: `mission-point-location-${location.id}`,
                mission_name: location.name
            }
        }));
        return [
            { type: 'FeatureCollection', features: pointFeatures },
            { type: 'FeatureCollection', features: [] }
        ];
    }

    // Full-detail path (high zoom)
    const durationFormatter = new FormatDurationPipe();

    const dateTimeFormat = dateFormatService.getDateTimeFormat();
    const timeFormat = dateFormatService.getTimeFormat();

    const { polygonFeatures, pointFeatures } = (
        locationList as MissionLocation[]
    ).reduce(
        (acc, location) =>
            location.missionList.reduce((acc2, m) => {
                const rpic = m.missionCrew.find(
                    c => c.missionRole.coreRole === 'PILOT_IN_COMMAND'
                )?.person;
                const startTime = moment.tz(m.missionDate, m.timeZone);

                const endTime = moment
                    .tz(m.missionDate, m.timeZone)
                    .add(m.missionEstimatedTime, 'seconds');
                const properties = {
                    id: `mission-${m.id}`,
                    DISP_ORDER: -moment(m.missionDate).unix(),
                    mission_id: m.id,
                    mission_name: m.name,
                    mission_date: moment
                        .tz(m.missionDate, m.timeZone)
                        .format(`${dateTimeFormat} z`),
                    mission_time: `${startTime.format(
                        timeFormat
                    )} - ${endTime.format(timeFormat)}`,
                    mission_duration: durationFormatter.transform(
                        m.missionEstimatedTime
                    ),
                    mission_rpic:
                        rpic != null
                            ? `${rpic.firstName} ${rpic.lastName}`
                            : '-',
                    mission_status: m.status,
                    mission_status_label: setupStatusFormatter(
                        combinedStatuses
                    )(m.status),
                    mission_status_category: getStatusCategory(m.status),
                    mission_point_of_contact:
                        m.pointOfContact ??
                        `${rpic?.firstName} ${rpic?.lastName}`,
                    mission_point_of_contact_information:
                        m.pointOfContactInformation
                };

                return {
                    polygonFeatures: acc2.polygonFeatures.concat({
                        type: 'Feature',
                        geometry: location.boundary,
                        id: `mission-boundary-${m.id}`,
                        properties
                    }),
                    pointFeatures: acc2.pointFeatures.concat({
                        type: 'Feature',
                        geometry: location.representativePoint,
                        id: `mission-point-${m.id}`,
                        properties
                    })
                };
            }, acc),
        { polygonFeatures: [] as Feature[], pointFeatures: [] as Feature[] }
    );

    return [
        {
            type: 'FeatureCollection',
            features: pointFeatures
        },
        {
            type: 'FeatureCollection',
            features: polygonFeatures
        }
    ];
}

/**
 * Make a form attachment URL resolver for the given form, which
 * may use the approval's authorities, or may use the default resolver.
 *
 * @param form the form to resovle the URLs for
 * @param missionApprovalList the list of approvals
 * @returns undefined (to use the default resolver) or a function to build the URL
 */
export function formAttachmentUrlFactory(
    form: ConcreteMappedEntityConcreteFormDto,
    missionApprovalList: RequestersMissionApprovalDto[] | undefined
): undefined | typeof defaultBuildFormAttachmentUrl {
    if (
        form.requiringEntityType !== 'MissionApproval' ||
        missionApprovalList == null
    ) {
        return undefined;
    }

    const approval = missionApprovalList.find(
        a => a.id === form.requiringEntityId
    );
    if (approval == null) {
        return undefined;
    }

    return (
        managingOrganisationId: number,
        formId: number,
        attachmentId: number,
        attachmentVersionId: number
    ) => {
        const params = httpParamSerializer({
            attachmentVersionId,
            managingOrganisationId
        }).toString();
        return `/webapi/organisationAuthorities/${approval.authorityId}/workflow/${approval.authorityWorkflowVersionId}/forms/${formId}/attachments/${attachmentId}?${params}`;
    };
}

/**
 * Takes a mission approval and converts the authority on the approval to a simple authority.
 * This is mainly used in the templates to convert the approval to a suitable format fot the formatting pipes.
 * @param approval a MissionApprovalWithAuthority object
 * @returns a SimpleAuthority object for the given approval.
 */
export function getSimplifiedAuthorityFromApproval(
    approval: MissionApprovalWithAuthority,
    organisation: SimpleOrganisationDto
) {
    if (
        approval == null ||
        approval.authority == null ||
        approval.authorityType == null
    ) {
        return;
    }
    return toSimpleAuthority(
        approval.authority,
        organisation,
        approval.authorityType
    );
}

export function findActiveMissionApprovals(
    approvals: MissionApprovalWithAuthority[],
    includeDrafts = false
) {
    const status = RequestersMissionApprovalDto.Status;
    const activeStatuses = [
        status.APPROVED,
        status.ACCEPTED,
        status.PENDING,
        status.BEING_REVIEWED
    ];
    if (includeDrafts) {
        activeStatuses.push(status.DRAFT);
    }
    return approvals.filter(a => activeStatuses.includes(a.approval.status));
}

/**
 * getMonthRange
 * @param timestamp For example: "2024-12-27T23:45:23.864Z"
 * @returns Returns an object containing the start and end timestamps (in UTC) of the current month (ISO string format).
 * /*
 * Test Example:
 * const testInput = "2024-12-27T23:45:23.864Z";
 * const result = getMonthRange(testInput);
 *
 * Example Result:
 * {
 *   startOfMonth: "2024-12-01T00:00:00.000Z",
 *   endOfMonth:   "2024-12-31T23:59:59.999Z"
 * }
 * */
export function getMonthRange(timestamp: string) {
    const dateMoment = moment.utc(timestamp);

    const startOfMonth = dateMoment.clone().startOf('month').toISOString();
    const endOfMonth = dateMoment.clone().endOf('month').toISOString();

    return {
        startOfMonth,
        endOfMonth
    };
}

export function getEffectiveStartTime(
    flight: SortieDto | CompleteSortieCommand
): Date | null {
    if (flight.durationSource === 'MANUAL') {
        return flight.manualStartTime ? new Date(flight.manualStartTime) : null;
    }
    return (flight as SortieDto).startTime
        ? new Date((flight as SortieDto).startTime)
        : null;
}

export function checkFlightsSorted(
    flightValues: SortieDto[] | CompleteSortieCommand[]
): boolean {
    for (let i = 0; i < flightValues.length - 1; i++) {
        const timeA = getEffectiveStartTime(flightValues[i]);
        const timeB = getEffectiveStartTime(flightValues[i + 1]);
        if (timeA && timeB && timeA.getTime() > timeB.getTime()) return false;
        if (!timeA && timeB) return false;
    }
    return true;
}

const statusOrder: { [status: string]: number } = {
    [MissionNoticeDto.Status.BLOCK]: 0,
    [MissionNoticeDto.Status.APPLICABLE]: 1,
    [MissionNoticeDto.Status.INFORMATIONAL]: 2
};

/**
 * An array.sort compatible comparator for MissionNoticeDto to sort by
 * status, then start time.
 *
 * @param a the first notice
 * @param b the second notice
 * @returns the comparator result
 */
export function missionNoticeSort(a: MissionNoticeDto, b: MissionNoticeDto) {
    const statusDiff = statusOrder[a.status] - statusOrder[b.status];
    if (statusDiff !== 0) {
        return statusDiff;
    }
    return (
        new Date(a.notice.startTime).getTime() -
        new Date(b.notice.startTime).getTime()
    );
}
